import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CameraAltRounded as CameraAltRoundedIcon,
  CameraswitchRounded as CameraswitchRoundedIcon,
} from "@mui/icons-material";

// ─────────────────────────────────────────────────────────────────────────────
// normalizeImageFile
// Reads a user-selected image File and returns an upright base64 data URL.
// Phone photos embed an EXIF "orientation" tag instead of physically rotating
// the pixels — which is why gallery uploads appear rotated 90°. We use
// createImageBitmap({ imageOrientation: "from-image" }) so the browser bakes the
// orientation into the pixels, then re-encode (which also strips the now-stale
// EXIF tag so nothing rotates it a second time). Optionally downscales large
// images. Falls back to a plain data URL on older browsers.
// ─────────────────────────────────────────────────────────────────────────────
export async function normalizeImageFile(
  file,
  { maxDim = 2000, quality = 0.9 } = {},
) {
  if (!file) return null;

  try {
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      });
      let width = bitmap.width;
      let height = bitmap.height;
      if (Math.max(width, height) > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0, width, height);
      if (bitmap.close) bitmap.close();
      return canvas.toDataURL("image/jpeg", quality);
    }
  } catch (err) {
    // Fall through to the plain reader below.
    console.warn("Image orientation normalisation failed, using raw file:", err);
  }

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CameraCaptureDialog
// A live-camera capture dialog that works on BOTH desktop (webcam) and mobile
// (device camera) via getUserMedia. Frames captured from the video stream are
// already upright, so no EXIF correction is needed for these. Returns a JPEG
// data URL through onCapture.
// ─────────────────────────────────────────────────────────────────────────────
export const CameraCaptureDialog = ({
  open,
  onClose,
  onCapture,
  title = "Take Photo",
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startStream = useCallback(
    async (mode) => {
      setError("");
      setStarting(true);
      stopStream();
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera not supported on this device/browser.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: mode ? { facingMode: { ideal: mode } } : true,
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          setHasMultipleCameras(
            devices.filter((d) => d.kind === "videoinput").length > 1,
          );
        } catch {
          /* ignore enumerate errors */
        }
      } catch (err) {
        setError(
          err?.name === "NotAllowedError"
            ? "Camera permission denied. Please allow camera access and retry."
            : err?.message || "Unable to access the camera.",
        );
      } finally {
        setStarting(false);
      }
    },
    [stopStream],
  );

  useEffect(() => {
    if (open) startStream(facingMode);
    else stopStream();
    return () => stopStream();
    // Intentionally keyed on `open` only — facing changes go through handleSwitch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSwitch = () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    startStream(next);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopStream();
    onCapture(dataUrl);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        {title}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: 280,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {error ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ color: "#fff", mb: 2 }}>{error}</Typography>
              <Button
                variant="outlined"
                onClick={() => startStream(facingMode)}
                sx={{ color: "#fff", borderColor: "#fff" }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  maxHeight: "60vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              {starting && (
                <Box sx={{ position: "absolute" }}>
                  <CircularProgress sx={{ color: "#fff" }} />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        {hasMultipleCameras && !error && (
          <Button
            startIcon={<CameraswitchRoundedIcon />}
            onClick={handleSwitch}
            sx={{ textTransform: "none", mr: "auto" }}
          >
            Switch camera
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CameraAltRoundedIcon />}
          onClick={handleCapture}
          disabled={!!error || starting}
          sx={{ textTransform: "none", minWidth: 130 }}
        >
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );
};
